import { useState } from "react";
import { Smartphone, Share, Menu } from "lucide-react";

/**
 * InstallInstructions
 * Compact card that walks a user through adding the app to their phone home screen.
 * Bilingual (EN / ES) with a toggle. Designed to fit inside Announcements,
 * the sign-in poster, or any onboarding surface.
 *
 * Props:
 *   variant?: "card" | "flat"     — "card" wraps in the app card style; "flat" is transparent (for poster print).
 *   defaultLang?: "en" | "es"     — Which language to show first (default "en").
 */
const COPY = {
  en: {
    eyebrow: "Install on your phone",
    title: "Add RollOSS to your Home Screen",
    intro: "It launches like a real app — full-screen, offline-ready, push notifications for class reminders.",
    iosTitle: "iPhone · Safari",
    iosSteps: [
      "Open getrolloss.app in Safari",
      "Tap the Share icon at the bottom",
      "Scroll down and tap “Add to Home Screen”",
      "Tap Add — the RollOSS icon appears on your home screen",
    ],
    androidTitle: "Android · Chrome",
    androidSteps: [
      "Open getrolloss.app in Chrome",
      "Tap the ⋮ menu (top-right)",
      "Tap “Install app” or “Add to Home Screen”",
      "Tap Install — the RollOSS icon appears on your home screen",
    ],
    footnote: "Chrome may show a bottom banner “Install RollOSS”. Tap it — same result.",
    langToggle: "Español",
  },
  es: {
    eyebrow: "Instalar en tu teléfono",
    title: "Agregar RollOSS a la Pantalla de Inicio",
    intro: "Se abre como una app real — pantalla completa, sin conexión, recordatorios de clase por notificación.",
    iosTitle: "iPhone · Safari",
    iosSteps: [
      "Abre getrolloss.app en Safari",
      "Toca el ícono Compartir en la parte inferior",
      "Desliza hacia abajo y toca “Agregar a Inicio”",
      "Toca Agregar — el ícono de RollOSS aparece en tu pantalla",
    ],
    androidTitle: "Android · Chrome",
    androidSteps: [
      "Abre getrolloss.app en Chrome",
      "Toca el menú ⋮ (arriba a la derecha)",
      "Toca “Instalar app” o “Agregar a inicio”",
      "Toca Instalar — el ícono de RollOSS aparece en tu pantalla",
    ],
    footnote: "Chrome puede mostrar un banner “Instalar RollOSS”. Tócalo — mismo resultado.",
    langToggle: "English",
  },
};

export default function InstallInstructions({ variant = "card", defaultLang = "en" }) {
  const [lang, setLang] = useState(defaultLang);
  const c = COPY[lang];
  const wrapClass = variant === "card" ? "card p-5" : "";

  return (
    <div className={wrapClass} data-testid="install-instructions">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="label" style={{ color: "var(--green)" }}>{c.eyebrow}</div>
          <div className="text-[16px] sm:text-[18px] font-bold uppercase tracking-tight mt-1">{c.title}</div>
        </div>
        <button
          onClick={() => setLang(lang === "en" ? "es" : "en")}
          className="label-tiny underline shrink-0"
          style={{ color: "var(--text-dim)" }}
          data-testid="install-lang-toggle"
        >
          {c.langToggle}
        </button>
      </div>

      <p className="label-tiny leading-relaxed" style={{ color: "var(--text-dim)" }}>{c.intro}</p>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div className="card p-4" data-testid="install-ios">
          <div className="flex items-center gap-2 mb-2">
            <Share size={14} style={{ color: "var(--green)" }} />
            <div className="label" style={{ color: "var(--text)" }}>{c.iosTitle}</div>
          </div>
          <ol className="list-decimal pl-4 space-y-1.5 label-tiny leading-relaxed" style={{ color: "var(--text-dim)" }}>
            {c.iosSteps.map((step) => <li key={step}>{step}</li>)}
          </ol>
        </div>

        <div className="card p-4" data-testid="install-android">
          <div className="flex items-center gap-2 mb-2">
            <Menu size={14} style={{ color: "var(--green)" }} />
            <div className="label" style={{ color: "var(--text)" }}>{c.androidTitle}</div>
          </div>
          <ol className="list-decimal pl-4 space-y-1.5 label-tiny leading-relaxed" style={{ color: "var(--text-dim)" }}>
            {c.androidSteps.map((step) => <li key={step}>{step}</li>)}
          </ol>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 label-tiny" style={{ color: "var(--text-muted)" }}>
        <Smartphone size={12} />
        <span>{c.footnote}</span>
      </div>
    </div>
  );
}
